import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, Button, Alert, CircularProgress, Card, CardContent, CardHeader, Divider } from '@mui/material';
import { CheckIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import temporaryNumberingService from '../../services/temporaryNumberingService';
import PageWrapper from '../common/PageWrapper';

interface ICMCounts {
  Frame: number;
  Panel: number;
  'Panel Window': number;
  'Net Fixing': number;
}

const ICMSettings: React.FC = () => {
  const [icmCounts, setIcmCounts] = useState<ICMCounts>({
    Frame: 0,
    Panel: 0,
    'Panel Window': 0,
    'Net Fixing': 0
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Load current ICM counts on component mount
  useEffect(() => {
    loadCurrentCounts();
  }, []);

  const loadCurrentCounts = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await temporaryNumberingService.getLatestICMCounts();
      if (response.success) {
        setIcmCounts({
          Frame: response.data.Frame || 0,
          Panel: response.data.Panel || 0,
          'Panel Window': response.data['Panel Window'] || 0,
          'Net Fixing': response.data['Net Fixing'] || 0
        });
      } else {
        setError('Failed to load current ICM counts');
      }
    } catch (error) {
      console.error('Error loading ICM counts:', error);
      setError('Failed to load current ICM counts');
    } finally {
      setLoading(false);
    }
  };

  const handleCountChange = (itemType: keyof ICMCounts, value: string) => {
    const numValue = parseInt(value) || 0;
    setIcmCounts(prev => ({
      ...prev,
      [itemType]: Math.max(0, numValue) // Ensure non-negative values
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await temporaryNumberingService.updateICMCounts(icmCounts);
      
      if (response.success) {
        setSuccess('ICM counts updated successfully!');
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError('Failed to save ICM counts');
      }
    } catch (error) {
      console.error('Error saving ICM counts:', error);
      setError('Failed to save ICM counts');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    loadCurrentCounts();
  };

  return (
    <PageWrapper >
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-secondary-900 dark:text-white">
          Item Count Master (ICM) Settings
        </h3>
        <p className="mt-1 text-sm text-secondary-500 dark:text-secondary-400">
          Initialize or update the starting counts for item number generation. These values will be used as the base for generating new item numbers.
        </p>
      </div>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      <Card>
        <CardHeader>
          <Typography variant="h6" component="h4">
            Current ICM Counts
          </Typography>
          <Typography variant="body2" color="text.secondary">
            These are the current values in the ICM table. Modify them to set new starting points for item numbering.
          </Typography>
        </CardHeader>
        <Divider />
        <CardContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-900 dark:text-white mb-2">
                    Frame Count
                  </label>
                  <TextField
                    type="number"
                    value={icmCounts.Frame}
                    onChange={(e) => handleCountChange('Frame', e.target.value)}
                    size="small"
                    fullWidth
                    inputProps={{ min: 0 }}
                    helperText="Starting count for Frame items (e.g., F-001, F-002)"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-900 dark:text-white mb-2">
                    Panel Count
                  </label>
                  <TextField
                    type="number"
                    value={icmCounts.Panel}
                    onChange={(e) => handleCountChange('Panel', e.target.value)}
                    size="small"
                    fullWidth
                    inputProps={{ min: 0 }}
                    helperText="Starting count for Panel items (e.g., P-001, P-002)"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-900 dark:text-white mb-2">
                    Panel Window Count
                  </label>
                  <TextField
                    type="number"
                    value={icmCounts['Panel Window']}
                    onChange={(e) => handleCountChange('Panel Window', e.target.value)}
                    size="small"
                    fullWidth
                    inputProps={{ min: 0 }}
                    helperText="Starting count for Panel Window items (e.g., PW-001, PW-002)"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-900 dark:text-white mb-2">
                    Net Fixing Count
                  </label>
                  <TextField
                    type="number"
                    value={icmCounts['Net Fixing']}
                    onChange={(e) => handleCountChange('Net Fixing', e.target.value)}
                    size="small"
                    fullWidth
                    inputProps={{ min: 0 }}
                    helperText="Starting count for Net Fixing items (e.g., N-001, N-002)"
                  />
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-between items-center pt-4">
        <Button
          variant="outlined"
          startIcon={<ArrowPathIcon className="h-4 w-4" />}
          onClick={handleReset}
          disabled={loading || saving}
        >
          Reset to Current
        </Button>

        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} /> : <CheckIcon className="h-4 w-4" />}
          onClick={handleSave}
          disabled={loading || saving}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">
          How it works:
        </h4>
        <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
          <li>• When creating new orders, item numbers will start from these counts + 1</li>
          <li>• If Frame count is 5, new Frame items will be F-006, F-007, etc.</li>
          <li>• If not set, the system will use existing ICM table values</li>
          <li>• Changes take effect immediately for new orders</li>
        </ul>
      </div>
    </div>
    </PageWrapper>
  );
};

export default ICMSettings;
